import { useForm } from 'react-hook-form';
import firebaseService from '@services/firebase';
import { EMAIL_REGEX } from '@monofront/business/constants';
import { useRedirect } from '@utils/redirect';
import { Dispatch, SetStateAction, useEffect, useState, type JSX } from 'react';
import { useTranslations } from 'use-intl';
import useFirebaseErrors from '@hooks/vendor/useFirebaseErrors';
import { useReferralContext } from '@context/referral';
import {
  ErrorText,
  Input,
  InputPassword,
  TextInput,
} from '@monofront/design-system/components/FormV2';
import Loader from '@monofront/design-system/components/Button/Loader';
import { getAuth, signInWithEmailAndPassword } from '@firebase/auth';

interface ISignInFormData {
  email: string;
  password: string;
}

interface IFormSignIn {
  toggleResetPasswordForm: Dispatch<SetStateAction<boolean>>;
  formCss: JSX.Element;
  defaultValues?: Partial<ISignInFormData>;
}

const FormSignIn: React.FC<IFormSignIn> = ({
  toggleResetPasswordForm,
  defaultValues,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors, touchedFields, dirtyFields },
    trigger,
  } = useForm<ISignInFormData & { formSignIn: void }>({
    defaultValues,
    mode: 'onChange',
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const isValidEmail =
    (!!defaultValues?.email || 'email' in dirtyFields) && !errors.email;
  const isValidPassword = 'password' in dirtyFields && !errors.password;
  const { MAPPING_SIGIN_ERROR } = useFirebaseErrors();
  const { redirectToHomePageOrReferrer, redirectToSubscriptionSummary } =
    useRedirect();
  const { isFromReferral } = useReferralContext();

  useEffect(() => {
    // triggers validation for defaultValues
    trigger();
  }, [trigger]);

  const tAccount = useTranslations('Account');

  const handleSignInUser = async (user) => {
    try {
      setIsProcessing(true);
      const credentials = await signInWithEmailAndPassword(
        getAuth(firebaseService.app),
        user.email,
        user.password,
      );

      if (isFromReferral) {
        await redirectToSubscriptionSummary();
      } else {
        await redirectToHomePageOrReferrer(credentials.user);
      }
    } catch (error: any) {
      setIsProcessing(false);
      setError('formSignIn', { type: error.code, message: error.message });
    }
  };

  return (
    <>
      {errors.formSignIn && (
        <div className="error">
          <p className="error tw-lf-error-message">
            {MAPPING_SIGIN_ERROR[errors.formSignIn.type as string]}
          </p>
        </div>
      )}
      <form
        onSubmit={handleSubmit(handleSignInUser)}
        className="tw-flex tw-flex-col tw-gap-4"
      >
        <TextInput
          id="register-email"
          isValid={isValidEmail}
          isInvalid={!!(touchedFields?.email && errors.email)}
        >
          <Input
            type="email"
            aria-label={tAccount('email')}
            placeholder={tAccount('email')}
            {...register('email', {
              required: true,
              pattern: EMAIL_REGEX,
              onChange: (e) => {
                setValue('email', e.target.value.toLowerCase());
                clearErrors('formSignIn');

                return trigger('email');
              },
            })}
          />
          {touchedFields?.email && errors.email && (
            <ErrorText>{tAccount('pleaseUseAValidEmail')}</ErrorText>
          )}
        </TextInput>
        <TextInput
          id="password"
          aria-label={tAccount('password')}
          isInvalid={!!(touchedFields?.password && errors.password)}
        >
          <InputPassword
            aria-label={tAccount('password')}
            placeholder={tAccount('password')}
            {...register('password', {
              required: true,
              onChange: () => {
                clearErrors('formSignIn');

                return trigger('password');
              },
            })}
          />
          {!!(touchedFields?.password && errors.password) && (
            <ErrorText>{tAccount('pleaseUseAValidPassword')}</ErrorText>
          )}
        </TextInput>

        <button
          className="tw-lf-button tw-lf-button-primary tw-lf-button-md tw-w-full"
          type="submit"
          disabled={!(isValidEmail && isValidPassword)}
        >
          {isProcessing ? (
            <span className="[&_span:top-0] tw-relative">
              <Loader />
            </span>
          ) : (
            tAccount('continue')
          )}
        </button>

        <a
          className="tw-lf-link-md"
          onClick={() => toggleResetPasswordForm(true)}
        >
          {tAccount('forgottenPassword')}
        </a>
      </form>
    </>
  );
};

export default FormSignIn;
